import React, { useState, useEffect, useRef } from 'react';
import authService from './api-authorization/AuthorizeService'
import axios from 'axios'
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid';
import MUIDataTable, { ExpandButton } from "mui-datatables";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const muiCache = createCache({
    key: 'mui-datatables',
    prepend: true,
});

export default function DBUpdateRecords() {
    const [token, setToken] = useState();
    const [days, setDays] = useState(1);
    const [status, setStatus] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [appData, setAppData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(false);
    const fileInputRef = useRef(null);
    const apiPath = 'api/PlayerTableUpdateRecords';

    const columns = [
        {
            name: '',
            label: '',
            options: {
                filter: false,
                display: true,
                viewColumns: false,
                customBodyRender: (value, tableMeta, update) => {
                    let rowIndex = Number(tableMeta.rowIndex) + 1;
                    return (
                        <div style={{ textAlign: "right" }}><span>{rowIndex}</span></div>
                    )
                }
            }
        },
        {
            name: "updatedOn",
            label: "Date",
            options: {
                sort: true,
                sortThirdClickReset: true,
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
                customBodyRender: (value, tableMeta, updateValue) => (
                    <div style={{ textAlign: "left" }}>{(new Date(value + "Z")).toLocaleString()}</div>
                )
            }
        },
        {
            name: "updateType",
            label: "Type",
            options: {
                sort: true,
                sortThirdClickReset: true,
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
                customBodyRender: (value, tableMeta, updateValue) => (
                    <div style={{ textAlign: "left" }}>{value === 1 ? "FIDE" : (value === 2 ? "USCF" : (value === 3 ? "Player" : (value === 4 ? "Top FIDE" : (value === 5 ? "Top SCF" : "Over Rated"))))}</div>
                )
            }
        },
        {
            name: "updateStatus",
            label: "Status",
            options: {
                sort: true,
                sortThirdClickReset: true,
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
            }
        },
        {
            name: "numberOfRecords",
            label: "Records",
            options: {
                sort: true,
                sortThirdClickReset: true,
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
            }
        },
        {
            name: "totalSeconds",
            label: "Seconds",
            options: {
                sort: true,
                sortThirdClickReset: true,
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
            }
        },
        {
            name: "errorMessage",
            label: "Message",
            options: {
                sort: true,
                sortThirdClickReset: true
            }
        },
    ];

    const options = {
        filter: true,
        filterType: 'dropdown',
        responsive: 'vertical',
        expandableRows: false,
        selectableRows: 'none',
        expandableRowsOnClick: false,
        expandableRowsHeader: false,
        download: false,
        print: false,
        rowsPerPage: 10,
        rowsPerPageOptions: [5, 10, 15, 25, 50, 100, 200],
        jumpToPage: true,
        fixedHeaderOptions: {
            xAxis: true,
            yAxis: true
        },
        resizableColumns: false,
        isRowExpandable: (dataIndex, expandedRows) => {
            // Prevent expand/collapse of any row if there are 4 rows expanded already (but allow those already expanded to be collapsed)
            if (expandedRows.data.length > 4 && expandedRows.data.filter(d => d.dataIndex === dataIndex).length === 0)
                return false;
            return true;
        },
        rowsExpanded: [],
        onRowExpansionChange: (curExpanded, allExpanded, rowsExpanded) =>
            console.log(curExpanded, allExpanded, rowsExpanded),
    };

    const theme = createTheme({
        overrides: {
            MUIDataTableSelectCell: {
                expandDisabled: {
                    // Soft hide the button.
                    visibility: 'hidden',
                },
            },
        },
    });

    const components = {
        ExpandButton: function (props) {
            return <ExpandButton {...props} />;
        },
    };

    useEffect(() => {
        async function RunOnce() {
            setToken(await authService.getAccessToken());
        }
        RunOnce();
    }, []); //Runs only on the first render

    const getAppData = async (e, playerName) => {
        e.preventDefault();
        setToken(await authService.getAccessToken());
        var headers = {
            headers: { 'Authorization': `Bearer ${token}` }
        }

        axios.get(apiPath, headers).then((response) => {
            setAppData(response.data);
            setLoading(false);
            setStatus("");
        }).catch((error) => {
            setLoading(false);
            setStatus("Error - Please check log for details.");
            console.log(JSON.stringify(error));
        });
    };

    const updateUSCF = async (e) => {
        e.preventDefault();
        var t = await authService.getAccessToken();
        var headers = {
            headers: { 'Authorization': `Bearer ${t}` }
        }
        setStatus("");
        setErrorMessage("");
        axios.get('/api/USCFPlayers/UpdateUSCFPlayerTable?forceUpdate=' + forceUpdate, headers).then((response) => {
            setStatus(response.data);
            setErrorMessage("");
            setImporting(false);
        }).catch((error) => {
            setImporting(false);
            setStatus("");
            setErrorMessage(error);
            console.log(JSON.stringify(error));
        });
    };

    const handleUploadUSCFClick = () => {
        fileInputRef.current.click();
    };

    const uploadUSCFFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setStatus("");
        setErrorMessage("");

        try {
            var t = await authService.getAccessToken();
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post('/api/USCFPlayers/UploadUSCFPlayerFile', formData, {
                headers: {
                    'Authorization': `Bearer ${t}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setStatus(`Upload successful! Imported ${response.data.recordsImported} records for ${response.data.ratingListMonth} in ${response.data.secondsElapsed.toFixed(1)} seconds.`);
            setErrorMessage("");
        } catch (error) {
            setStatus("");
            if (error.response && error.response.data) {
                const errData = error.response.data;
                setErrorMessage(errData.error || JSON.stringify(errData));
            } else {
                setErrorMessage(error.message || JSON.stringify(error));
            }
            console.log(JSON.stringify(error));
        } finally {
            setUploading(false);
            // Reset file input so the same file can be selected again if needed
            e.target.value = '';
        }
    };

    const updateFIDE = async (e) => {
        e.preventDefault();
        var t = await authService.getAccessToken();
        var headers = {
            headers: { 'Authorization': `Bearer ${t}` }
        }
        setStatus("");
        setErrorMessage("");
        axios.get('/api/FIDEPlayers/UpdateFIDEPlayerTable?forceUpdate=' + forceUpdate, headers).then((response) => {
            setStatus(response.data);
            setErrorMessage("");
            setImporting(false);
        }).catch((error) => {
            setImporting(false);
            setStatus("");
            setErrorMessage(error);
            console.log(JSON.stringify(error));
        });
    };

    const updatePlayers = async (e) => {
        e.preventDefault();
        var t = await authService.getAccessToken();
        var headers = {
            headers: { 'Authorization': `Bearer ${t}` }
        }
        setStatus("");
        setErrorMessage("");
        axios.get('/api/ChessPlayers/UpdateChessPlayerTable?forceUpdate=' + forceUpdate, headers).then((response) => {
            setStatus(response.data);
            setErrorMessage("");
            setImporting(false);
        }).catch((error) => {
            setImporting(false);
            setStatus("");
            setErrorMessage(error);
            console.log(JSON.stringify(error));
        });
    };

    const updateTop = async (e) => {
        e.preventDefault();
        var t = await authService.getAccessToken();
        var headers = {
            headers: { 'Authorization': `Bearer ${t}` }
        }
        setStatus("");
        setErrorMessage("");
        axios.get('/api/FIDEPlayers/UpdateTopFIDEPlayerTable?forceUpdate=' + forceUpdate, headers).then((response) => {
            setStatus(response.data);
            setErrorMessage("");
            setImporting(false);
        }).catch((error) => {
            setImporting(false);
            setStatus("");
            setErrorMessage(error);
            console.log(JSON.stringify(error));
        });
    };

    const updateUnderrated = async (e) => {
        e.preventDefault();
        var t = await authService.getAccessToken();
        var headers = {
            headers: { 'Authorization': `Bearer ${t}` }
        }
        setStatus("");
        setErrorMessage("");
        axios.get('/api/ChessPlayers/UpdateTopValueChessPlayerTable', headers).then((response) => {
            setStatus(response.data);
            setErrorMessage("");
            setImporting(false);
        }).catch((error) => {
            setImporting(false);
            setStatus("");
            setErrorMessage(error);
            console.log(JSON.stringify(error));
        });
    };


    return (
        <Box sx={{ pt: 2, pb: 2 }}>
            <Grid container direction="row" rowSpacing={1} columnSpacing={1} align-items="flex-end">
                <Grid item xs={12} lg={2}>
                    <FormControlLabel control={<Checkbox checked={forceUpdate} onChange={e => { setForceUpdate(e.target.checked); }} />} label="Force Update" />
                </Grid>
                <Grid item xs={12} lg={2}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <LoadingButton color="success" fullWidth onClick={async (e) => { e.preventDefault(); await setImporting(true); await updateUSCF(e); }} loading={importing} loadingIndicator="Updating..." variant="contained">Update USCF</LoadingButton>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={2}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <LoadingButton color="success" fullWidth onClick={async (e) => { e.preventDefault(); await setImporting(true); await updateFIDE(e); }} loading={importing} loadingIndicator="Updating..." variant="contained">Update FIDE</LoadingButton>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={2}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <LoadingButton color="success" fullWidth onClick={async (e) => { e.preventDefault(); await setImporting(true); await updatePlayers(e); }} loading={importing} loadingIndicator="Updating..." variant="contained">Update Players</LoadingButton>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={2}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <LoadingButton color="success" fullWidth onClick={async (e) => { e.preventDefault(); await setImporting(true); await updateTop(e); }} loading={importing} loadingIndicator="Updating..." variant="contained">Update Top</LoadingButton>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={2}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <LoadingButton color="success" fullWidth onClick={async (e) => { e.preventDefault(); await setImporting(true); await updateUnderrated(e); }} loading={importing} loadingIndicator="Updating..." variant="contained">Underated</LoadingButton>
                    </Stack>
                </Grid>

                <Grid item xs={12} lg={3}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept=".tsv,.txt"
                            onChange={uploadUSCFFile}
                        />
                        <LoadingButton
                            color="warning"
                            fullWidth
                            onClick={handleUploadUSCFClick}
                            loading={uploading}
                            loadingIndicator="Uploading..."
                            variant="contained"
                        >
                            Manual Upload USCF File
                        </LoadingButton>
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={9}></Grid>

                <Grid item xs={12} lg={7} >
                    <TextField
                        size="small"
                        fullWidth
                        id="outlined-number"
                        label={"Player DB Updates In The Past " + days + " Day(s)"}
                        type="number"
                        inputProps={{ min: 1, max: 365 }}
                        InputLabelProps={{
                            shrink: true
                        }}
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} lg={5}>
                    <Stack spacing={1} direction="row" justifyContent="end">
                        <LoadingButton fullWidth onClick={async (e) => { e.preventDefault(); await setLoading(true); await getAppData(e, days); }} loading={loading} loadingIndicator="Loading..." variant="contained">Show Player DB Update Records</LoadingButton>
                        {/*<Button variant="outlined" onClick={async (e) => { e.preventDefault(); await setAppData([]); await setStatus(""); }}>{"Clear"}</Button>*/}
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <div>
                        <label>{status}</label>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <div>
                        <label style={{ color: 'red' }}>{errorMessage}</label>
                    </div>
                </Grid>

                {appData.length > 0 &&
                    <Grid item xs={12}>
                        <CacheProvider value={muiCache}>
                            <ThemeProvider theme={theme}>
                                <MUIDataTable
                                    /*title={appData.length + " updates in the past " + days + " day(s)"}*/
                                    title={
                                        <div>
                                            <Button variant="contained" color="secondary"
                                                onClick={async (e) => { e.preventDefault(); await setAppData([]); /*await setSelectedUser(); await setSearchString("")*/ }}>
                                                {"Clear Results (" + appData.length + ")"}
                                            </Button>
                                        </div>
                                    }
                                    data={appData}
                                    columns={columns}
                                    options={options}
                                    components={components}
                                />
                            </ThemeProvider>
                        </CacheProvider>
                    </Grid>
                }
            </Grid>

        </Box>
    )
}